import * as React from "react";
import { useTranslation } from "react-i18next";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { formatCompactNumber, formatCurrency } from "../utils";
import { useTopProviders, type MetricScope, type StatsFilters } from "../api";

type Props = { filters: StatsFilters; metricScope: MetricScope };

const COLORS = ["#86c9a6", "#b7e2cb", "#d5efe0", "#e9f6ee", "#5fa888"];
const SIZE_OPTIONS = [5, 10, 25, 50];

export function ProviderUsage({ filters, metricScope: selectedScope }: Props) {
  const { t } = useTranslation();
  const [size, setSize] = React.useState(5);
  const {
    data: response,
    isLoading,
    metricScope,
  } = useTopProviders(filters, size, selectedScope);

  const showSpend = metricScope !== "usage";
  const rows = (response?.data ?? []).map((row) => ({
    provider: row.provider ?? "unknown",
    spend: row.total_spend ?? 0,
    successful: row.successful_requests,
    failed: row.failed_requests,
    tokens: row.total_tokens,
  }));

  return (
    <SectionCard
      title={t("usageAndCost.providerUsage.title")}
      category={t("usageAndCost.categories.all")}
      headerExtra={
        <div
          role="tablist"
          aria-label={t("usageAndCost.providerUsage.sizeAriaLabel")}
          className="inline-flex max-w-full shrink-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-secondary p-1"
        >
          {SIZE_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={size === s}
              onClick={() => setSize(s)}
              className={cn(
                "shrink-0 rounded-lg px-2 py-0.5 text-xs font-medium transition-colors",
                size === s
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="px-5 py-3 text-start text-xs font-medium">
                {t("usageAndCost.providerUsage.provider")}
              </th>
              {showSpend && (
                <th className="px-5 py-3 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.spendUsd")}
                </th>
              )}
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.successful")}
              </th>
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.failed")}
              </th>
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.tokens")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: Math.min(size, 5) }).map((_, i) => (
                <tr key={i} className="border-b border-border last:border-b-0">
                  {Array.from({ length: showSpend ? 5 : 4 }).map((__, j) => (
                    <td key={j} className="px-5 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={showSpend ? 5 : 4}
                  className="px-5 py-10 text-center text-sm text-muted-foreground"
                >
                  {t("usageAndCost.providerUsage.noData")}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={`${row.provider}-${idx}`}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-2 text-foreground">
                      <span
                        className="inline-flex size-5 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                        style={{
                          backgroundColor: COLORS[idx % COLORS.length],
                        }}
                      >
                        {row.provider.charAt(0).toUpperCase()}
                      </span>
                      {row.provider}
                    </span>
                  </td>
                  {showSpend && (
                    <td className="px-5 py-3 text-end text-foreground">
                      {formatCurrency(row.spend)}
                    </td>
                  )}
                  <td className="px-5 py-3 text-end text-emerald-400">
                    {row.successful}
                  </td>
                  <td className="px-5 py-3 text-end text-destructive">
                    {row.failed}
                  </td>
                  <td className="px-5 py-3 text-end text-muted-foreground">
                    {formatCompactNumber(row.tokens)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
